import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import api, { fmtIDR, STATUS_LABELS, isInternal } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { ArrowDownRight, ArrowUpRight, FileWarning, Layers, TrendingUp } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { useNavigate } from "react-router-dom";
import { DashboardInsights } from "@/components/DashboardInsights";
import { ClientLogo } from "@/components/ClientLogo";

const STATUS_ORDER = ["draft", "review", "checked", "revisi", "approved", "archived"];
const MO = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];

export default function Dashboard() {
  const { user, selectedClientId, clients } = useAuth();
  const { t } = useI18n();
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState([]);
  const [pnl, setPnl] = useState([]);
  const navigate = useNavigate();

  const clientId = isInternal(user.role) ? selectedClientId : user.client_id;

  useEffect(() => {
    const q = isInternal(user.role) && clientId ? `?client_id=${clientId}` : "";
    api.get(`/transactions/summary${q}`).then(({ data }) => setSummary(data)).catch(() => {});
    const yr = new Date().getFullYear();
    const tq = isInternal(user.role) && clientId ? `?client_id=${clientId}&year=${yr}` : `?year=${yr}`;
    api.get(`/dashboard/cashflow-trend${tq}`).then(({ data }) => setTrend(data.data || [])).catch(() => {});
    if (isInternal(user.role)) api.get(`/dashboard/pnl-trend${tq}`).then(({ data }) => setPnl(data.data || [])).catch(() => {});
  }, [clientId, user.role]);

  const clientName = clients.find((c) => c.id === clientId)?.name || "";

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-center gap-3">
        {clientId && <ClientLogo id={clientId} v={clients.find((c) => c.id === clientId)?.logo_v} className="h-12 w-12" icon="h-5 w-5" />}
        <div>
          <h1 className="font-head text-2xl font-bold tracking-tight">{t("dash.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t("dash.welcome")}, {user.name}. {clientName && `${t("dash.clientContext")}: ${clientName}.`}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={ArrowUpRight} label={t("dash.in")} value={fmtIDR(summary?.total_in)} tone="emerald" testid="stat-in" />
        <StatCard icon={ArrowDownRight} label={t("dash.out")} value={fmtIDR(summary?.total_out)} tone="rose" testid="stat-out" />
        <StatCard icon={Layers} label={t("dash.total")} value={summary?.total ?? "—"} tone="blue" testid="stat-total" />
        <StatCard icon={FileWarning} label={t("dash.pending")} value={summary?.pending_docs ?? "—"} tone="amber" testid="stat-pending" />
      </div>

      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-head font-semibold text-lg">{t("dash.workflow")}</h2>
          <TrendingUp className="h-5 w-5 text-muted-foreground" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {STATUS_ORDER.map((s) => (
            <button
              key={s}
              onClick={() => navigate(`/transaksi?status=${s}`)}
              className="rounded-md border border-border p-4 text-left hover:bg-secondary/50 transition-colors"
              data-testid={`workflow-${s}`}
            >
              <div className="num text-2xl font-bold">{summary?.by_status?.[s] ?? 0}</div>
              <div className="mt-2"><StatusBadge status={s} /></div>
            </button>
          ))}
        </div>
      </Card>

      <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-head font-semibold text-lg">{t("dash.cashflow")} {new Date().getFullYear()}</h2>
          <TrendingUp className="h-5 w-5 text-muted-foreground" />
        </div>
        <div style={{ width: "100%", height: 300 }} data-testid="cashflow-chart">
          <ResponsiveContainer>
            <BarChart data={(trend || []).map((d) => ({ name: MO[d.month - 1], Masuk: d.masuk, Keluar: d.keluar }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 1e6 ? `${(v / 1e6).toFixed(0)}jt` : v)} />
              <Tooltip formatter={(v) => fmtIDR(v)} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Masuk" fill="#059669" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Keluar" fill="#e11d48" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {isInternal(user.role) && <Card className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-head font-semibold text-lg">{t("dash.pnl")} {new Date().getFullYear()}</h2>
          <TrendingUp className="h-5 w-5 text-muted-foreground" />
        </div>
        <div style={{ width: "100%", height: 300 }} data-testid="pnl-chart">
          <ResponsiveContainer>
            <BarChart data={(pnl || []).map((d) => ({ name: MO[d.month - 1], Pendapatan: d.pendapatan, Beban: d.beban }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 1e6 ? `${(v / 1e6).toFixed(0)}jt` : v)} />
              <Tooltip formatter={(v) => fmtIDR(v)} contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Pendapatan" fill="#2563eb" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Beban" fill="#f59e0b" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>}
      <DashboardInsights clientId={clientId} />
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone, testid }) {
  const tones = {
    emerald: "text-emerald-600 bg-emerald-100 dark:bg-emerald-950/60",
    rose: "text-rose-600 bg-rose-100 dark:bg-rose-950/60",
    blue: "text-blue-600 bg-blue-100 dark:bg-blue-950/60",
    amber: "text-amber-600 bg-amber-100 dark:bg-amber-950/60",
  };
  return (
    <Card className="p-5" data-testid={testid}>
      <div className={`h-9 w-9 rounded-md flex items-center justify-center ${tones[tone]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="mt-3 text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="num text-xl font-bold mt-1">{value}</div>
    </Card>
  );
}
